import { HTMLAttributes, useMemo } from 'react';
import classNames from 'classnames';
import colorThis from '@eknowles/color-this';
import Icon from 'components/atoms/Icon';
import { IconName } from 'components/atoms/Icon/all';
import { TagColor } from 'utils/frontendTypes';
import { twMerge } from 'tailwind-merge';

export interface TagProps extends HTMLAttributes<HTMLDivElement> {
  color?: TagColor;
  icon?: IconName;
  iconColor?: string;
  random?: boolean;
  size?: 'xs' | 'small';
  text?: string;
  truncate?: boolean;
}

const TagConfig = {
  suspended: { text: 'Suspended', color: '#b7791f' },
  active: { text: 'Active' },
  inactive: { text: 'Inactive' },
  canceled: { text: 'Canceled' },
  unsubscribed: { text: 'Unsubscribed' },
  expired: { text: 'Expired', color: '#d69e2e' },
  blocked: { text: 'Blocked', color: '#eb4512' },
  unpublished: { text: 'Unpublished', color: '#627CFE' },
  published: { text: 'Published' },
  completed: { text: 'Completed' },
  complete: { text: 'Complete' },
  monthly: { text: 'Monthly' },
  yearly: { text: 'Yearly' },
  bundle: { text: 'Bundle' },
  starter: { text: 'Starter' },
  one_time: { text: 'One Time' },
  past_due: { text: 'Past Due' },
  unpaid: { text: 'Unpaid' },
  incomplete: { text: 'Incomplete' },
  incomplete_expired: { text: 'Incomplete Expired' },
  trialing: { text: 'Trialing' },
  RCI: { text: 'RCI' },
  CHURCH: { text: 'Church' },
  keyword: { text: 'Keyword' },
  keywords: { text: 'Keywords' },
  series: { text: 'Series' },
  broadcast: { text: 'Broadcast' },
  broadcasts: { text: 'Broadcasts' },
  unknown: { text: 'Unknown', color: '#FAA81A' },
  submitted: { text: 'Submitted', color: '#627CFE' },
  pending_submission: { text: 'Pending Submission', color: '#d69e2e' },
  verified: { text: 'Verified', color: '#009B45' },
  unverified: { text: 'Unverified', color: '#DA605B' },
  glooVerificationRequested: { text: 'Verification Requested' },
  glooLegacyVerified: { text: 'Legacy Verified', color: '#009B45' },
  glooAdminRejected: { text: 'Admin Rejected', color: '#DA605B' },
  glooAdminVerified: { text: 'Admin Verified', color: '#009B45' },
  glooAutoRejected: { text: 'Auto Rejected', color: '#DA605B' },
  glooAutoVerified: { text: 'Auto Verified', color: '#009B45' },
};

function Tag({ className, color, icon, size, text, truncate, iconColor, ...rest }: TagProps) {
  const randomColor = useMemo(() => colorThis(`${text}`, 3), [text]);

  const colorClasses = () => {
    switch (color) {
      case 'blue':
        return 'bg-glooBlue-100 text-glooBlue-600';
      case 'yellow':
        return 'bg-yellow-200 text-yellow-900';
      case 'pink':
        return 'bg-pink-100 text-pink-600';
      case 'clearBlue':
        return 'bg-white text-blue rounded-sm border-blue border-2';
      case 'green':
        return 'bg-primary-100 text-primary-700';
      case 'darkGreen':
        return 'bg-primary-800 text-white';
      case 'greenBorder':
        return 'bg-white text-primary-700 border-green border-2';
      case 'greenInverted':
        return 'bg-white text-primary-700';
      case 'lightRed':
        return 'bg-orange-100 text-orange-700';
      case 'lightPurple':
        return 'bg-purpleLighter text-purpleDark';
      case 'lightPurpleInverted':
        return 'text-purpleLighter bg-purpleDark';
      case 'lightYellow':
        return 'bg-yellowLighter text-orangeDark';
      case 'red':
        return 'bg-redLighter text-white';
      case 'transparentBlue':
        return 'bg-blueLighter text-blue rounded-lg';
      case 'gray':
        return 'bg-glooGrayBorder text-glooCharcoal';
      case 'glooCharcoal':
        return 'bg-glooCharcoal text-white';
      case 'lightNavy':
        return 'bg-glooNavy-50 text-glooNavy-900';
      case 'darkNavy':
        return 'bg-glooNavy-900 text-white';
      case 'white':
        return 'bg-white text-glooNavy-900 border-glooNavy-100 border-2';
      case 'default':
        return 'bg-glooOffWhite-50 text-glooNavy-900';
      case 'inactive':
        return 'bg-glooGrayBorder text-white';
      case 'lightGray':
        return 'bg-neutral-100 text-neutral-800 border-neutral-400';
      default:
        return 'text-white';
    }
  };

  const iconColorClasses = () => {
    switch (iconColor) {
      case 'blue':
        return 'text-glooNavy-700';
      default:
        return color;
    }
  };

  const renderIcon = () => {
    const getIconSize = size === 'xs' ? 12 : 14;
    const getIconColor = iconColorClasses();

    return <Icon name={icon} size={getIconSize} className={getIconColor} />;
  };

  return (
    <div
      {...rest}
      className={twMerge(
        'px-2 font-medium flex items-center justify-center space-x-1',
        colorClasses(),
        classNames({
          'text-xs py-1 pl-3 pr-3': size === 'xs',
          'text-sm py-1 pl-5 pr-5': size === 'small',
        }),
        className,
      )}
      style={color ? {} : { backgroundColor: TagConfig[text]?.color || randomColor }}
    >
      {icon && renderIcon()}
      <span className={classNames({ 'min-w-0 truncate': truncate })}>{TagConfig[text]?.text || text}</span>
    </div>
  );
}

export default Tag;
